import React, { useEffect, useRef, useState } from 'react'

import type { Shipment } from '../../types/deal'
import { ShipmentEditor } from '../ShipmentEditor'

import { TEMP_SHIPMENT_PREFIX } from './shipmentDiff'

const CARRIER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 't-cat', label: '黑貓 (T-Cat)' },
  { value: 'fedex', label: 'FedEx' },
  { value: 'ups', label: 'UPS' },
  { value: 'dhl', label: 'DHL' },
]

interface ShipmentTabProps {
  shipments: Shipment[]
  highlightShipmentId?: string
  onChangeShipments: (next: Shipment[]) => void
  onError: (message: string) => void
}

export const ShipmentTab: React.FunctionComponent<ShipmentTabProps> = (props) => {
  const { highlightShipmentId, onChangeShipments, onError, shipments } = props
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const [highlightedId, setHighlightedId] = useState<string | null>(null)

  const [showForm, setShowForm] = useState(false)
  const [formRecipient, setFormRecipient] = useState('')
  const [formCarrier, setFormCarrier] = useState(CARRIER_OPTIONS[0].label)
  const [formMethod, setFormMethod] = useState<'warehouse' | 'customer'>('warehouse')
  const [formTracking, setFormTracking] = useState('')

  useEffect(() => {
    if (!highlightShipmentId) return undefined
    const element = cardRefs.current.get(highlightShipmentId)
    if (!element) return undefined
    element.scrollIntoView({ block: 'center', behavior: 'smooth' })
    setHighlightedId(highlightShipmentId)
    const handle = window.setTimeout(() => setHighlightedId(null), 2600)
    return () => window.clearTimeout(handle)
  }, [highlightShipmentId])

  function onAddShipment(): void {
    if (!formTracking.trim() && !formCarrier.trim()) {
      onError('Please pick a carrier and add a tracking number')
      return
    }
    // Local-only id until the server-side create returns a real ObjectId.
    const nextId = `${TEMP_SHIPMENT_PREFIX}${shipments.length + 1}`
    const now = new Date().toISOString()
    const newShipment: Shipment = {
      id: nextId,
      dealId: '',
      createdTime: now,
      updatedTime: now,
      label: `Shipment #${shipments.length + 1}`,
      carrier: formCarrier,
      trackingNumber: formTracking,
      linkedInvoiceNumber: '',
      devices: [],
      legs: [
        {
          type: formMethod,
          status: 'pending',
          statusDate: null,
          carrier: formMethod === 'customer' ? formCarrier : undefined,
          trackingNumber: formMethod === 'customer' ? formTracking : undefined,
        },
      ],
    }
    onChangeShipments([...shipments, newShipment])
    setShowForm(false)
    setFormRecipient('')
    setFormCarrier(CARRIER_OPTIONS[0].label)
    setFormMethod('warehouse')
    setFormTracking('')
  }

  function updateShipment(next: Shipment): void {
    onChangeShipments(shipments.map((entry) => (entry.id === next.id ? next : entry)))
  }

  function deleteShipment(shipmentId: string): void {
    onChangeShipments(shipments.filter((entry) => entry.id !== shipmentId))
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', margin: '16px 0' }}>
        {!showForm && (
          <button
            type="button"
            className="btn-text"
            onClick={() => setShowForm(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <svg
              width={13}
              height={13}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add Shipment
          </button>
        )}
      </div>

      {showForm && (
        <div
          style={{
            background: '#fafafa',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: 16,
            marginBottom: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600 }}>New shipment</div>
          <input
            type="text"
            value={formRecipient}
            onChange={(event) => setFormRecipient(event.target.value)}
            placeholder="Recipient (optional)"
            style={{
              height: 32,
              padding: '0 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 12,
              fontFamily: 'inherit',
            }}
          />
          <select
            value={formCarrier}
            onChange={(event) => setFormCarrier(event.target.value)}
            style={{
              height: 32,
              padding: '0 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 12,
              fontFamily: 'inherit',
              background: 'white',
            }}
          >
            {CARRIER_OPTIONS.map((entry) => (
              <option key={entry.value} value={entry.label}>
                {entry.label}
              </option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => setFormMethod('warehouse')}
              style={{
                flex: 1,
                padding: '10px 12px',
                borderRadius: 6,
                border: `1px solid ${formMethod === 'warehouse' ? '#1d1d1f' : 'var(--border)'}`,
                background: formMethod === 'warehouse' ? '#1d1d1f' : 'white',
                color: formMethod === 'warehouse' ? 'white' : '#475569',
                fontSize: 12,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ fontWeight: 600 }}>To Warehouse</div>
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>
                Two-leg: TW warehouse → customer
              </div>
            </button>
            <button
              type="button"
              onClick={() => setFormMethod('customer')}
              style={{
                flex: 1,
                padding: '10px 12px',
                borderRadius: 6,
                border: `1px solid ${formMethod === 'customer' ? '#1d1d1f' : 'var(--border)'}`,
                background: formMethod === 'customer' ? '#1d1d1f' : 'white',
                color: formMethod === 'customer' ? 'white' : '#475569',
                fontSize: 12,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ fontWeight: 600 }}>To Customer</div>
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>
                Single-leg domestic delivery
              </div>
            </button>
          </div>
          <input
            type="text"
            value={formTracking}
            onChange={(event) => setFormTracking(event.target.value)}
            placeholder="Tracking number"
            style={{
              height: 32,
              padding: '0 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 12,
              fontFamily: 'inherit',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="btn-ghost"
              style={{ fontSize: 12 }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onAddShipment}
              className="btn-save"
              style={{ fontSize: 12 }}
            >
              Add Shipment
            </button>
          </div>
        </div>
      )}

      {shipments.length === 0 && !showForm && (
        <div
          style={{
            border: '1px dashed var(--border)',
            borderRadius: 10,
            padding: '32px 24px',
            margin: '16px 0',
            textAlign: 'center',
            color: 'var(--secondary)',
            fontSize: 13,
          }}
        >
          No shipments yet. Click <strong>Add Shipment</strong> above to record one.
        </div>
      )}

      {shipments.map((shipment) => (
        <ShipmentEditor
          key={shipment.id}
          shipment={shipment}
          highlighted={highlightedId === shipment.id}
          onUpdate={updateShipment}
          onDelete={() => deleteShipment(shipment.id)}
          registerRef={(element) => {
            if (element) cardRefs.current.set(shipment.id, element)
            else cardRefs.current.delete(shipment.id)
          }}
        />
      ))}
    </div>
  )
}
